import { configureStore, createSlice } from '@reduxjs/toolkit'
import { persistReducer, persistStore } from 'redux-persist'
import storage from 'redux-persist/lib/storage' // Defaults to localStorage for web

/**
 * Represents the state of an invoice.
 *
 * @typedef {Object} invoiceState
 * @property {boolean} isInvoice - Indicates whether the entity is an invoice.
 * @property {string} clientId - Unique identifier for the client associated with the invoice.
 * @property {string} documentId - Unique identifier for the invoice document.
 */
export type invoiceState = {
  isInvoice: boolean
  // Received from URL.
  clientId: string
  documentId: string
  // Received from authenticate.
  contactId?: string
}

export type invoiceStateAction = {
  invoice: invoiceState
}

/**
 * Represents the initial state of an invoice.
 * This object defines default values for the invoice's state attributes.
 *
 * @typedef {Object} invoiceState
 * @property {boolean} isInvoice - Indicates whether the invoice is valid or active. Defaults to false.
 * @property {string} clientId - Stores the client identifier. Defaults to an empty string.
 * @property {string} documentId - Stores the document identifier. Defaults to an empty string.
 *
 * @type {invoiceState}
 */
const initialInvoiceState: invoiceState = { isInvoice: false, clientId: '', documentId: '', contactId: '' }

/**
 * A Redux slice for managing invoice-related state.
 *
 * This slice manages the state of invoices, including whether an invoice exists
 * and related client and document identifiers. It provides a reducer to update
 * the state with invoice data.
 *
 * Properties:
 * - name: Specifies the name of the slice, which is 'invoice' in this case.
 * - initialState: Represents the initial state of the slice, corresponding to
 *   the `initialInvoiceState` object.
 * - reducers: Contains methods for updating the slice's state. This includes:
 *   - invoice: A reducer function for identifying the presence of an invoice
 *     and updating the associated client and document IDs.
 */
const invoiceSlice = createSlice({
  name: 'invoice',
  initialState: initialInvoiceState,
  reducers: {
    invoice: (state, action) => {
      state.isInvoice = true
      state.clientId = action.payload.clientId
      state.documentId = action.payload.documentId
      state.contactId = action.payload.contactId
    },
    reset: () => initialInvoiceState,
  },
})

const persistConfig = {
  key: 'root', // Key used for storage
  storage,
}
const persistedReducer = persistReducer(persistConfig, invoiceSlice.reducer)

/**
 * The Redux store configured with the reducer specified in the configuration.
 * It manages the application state related to invoices using the `invoiceSlice` reducer.
 *
 * This store serves as the central place for managing state in the application,
 * enabling the use of Redux's state management capabilities such as dispatching actions,
 * subscribing to store updates, and accessing the current state.
 */
const store = configureStore({
  reducer: { invoice: persistedReducer },
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({ serializableCheck: false }),
})

export const { invoice, reset } = invoiceSlice.actions
export const persistor = persistStore(store)

export default store
